import { useState, useRef } from "react";
import { ISourceAuction } from "../../../services";

/**
 * Custom hook to manage the form state and functionality for the Returns component
 */
export function useReturnsForm(rowData: ISourceAuction[]) {
  // State for quantity values
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  
  // Track which auction is in edit mode
  const [editingAuctionId, setEditingAuctionId] = useState<string | null>(null);
  
  // Flag to track if we're in the process of cancelling an edit
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Create a ref for the active input field
  const activeInputRef = useRef<HTMLInputElement | null>(null);

  /**
   * Handler for starting edit mode for a specific auction
   */
  const handleStartEdit = (auctionId: string) => {
    // Save only the current auction's quantity and clear all others
    const currentQuantity = quantities[auctionId] || "";

    // Clear all quantities except for the one being edited
    setQuantities({
      [auctionId]: currentQuantity,
    });

    setEditingAuctionId(auctionId);
    // Reset cancellation flag when starting a new edit
    setIsCancelling(false);

    // Focus the input after a short delay to ensure it's rendered
    setTimeout(() => {
      if (activeInputRef.current) {
        activeInputRef.current.focus();

        // Set cursor at the end of the input value
        const inputElement = activeInputRef.current;
        inputElement.selectionStart = currentQuantity.length;
        inputElement.selectionEnd = currentQuantity.length;
      }
    }, 50);
  };

  /**
   * Handler for canceling edit mode
   */
  const handleCancelEdit = () => {
    if (editingAuctionId) {
      // Clear the quantity for this auction when canceling
      setQuantities((prev) => {
        const newQuantities = { ...prev };
        delete newQuantities[editingAuctionId];
        return newQuantities;
      });

      // Immediately clear the input field if it exists
      if (activeInputRef.current) {
        activeInputRef.current.value = "";
      }
    }

    // Exit edit mode
    setEditingAuctionId(null);
    // Reset cancellation flag
    setIsCancelling(false);
  };

  /**
   * Handler for saving a value after edit
   */
  const handleSaveValue = (auctionId: string, value: string) => {
    // Don't save if we're in the middle of cancelling
    if (isCancelling) return;

    // Validate the value is a valid number with up to 3 decimal places and greater than zero
    if (value && /^\d*\.?\d{0,3}$/.test(value) && parseFloat(value) > 0) {
      setQuantities((prev) => ({
        ...prev,
        [auctionId]: value,
      }));
    }

    // Exit edit mode
    setEditingAuctionId(null);
  };

  /**
   * Function to check if any quantities exceed capacity
   * Returns array of auctions with quantities that exceed capacity
   */
  const getExceedingCapacityAuctions = () => {
    // Store auction IDs and quantity values that exceed capacity
    const exceedingAuctions: {
      auctionId: string;
      value: string;
      maxCapacity: number;
    }[] = [];

    // Check all quantities, including the one being edited right now
    const allQuantities = { ...quantities };

    // First add the currently edited value if there is one
    if (editingAuctionId && activeInputRef.current?.value) {
      allQuantities[editingAuctionId] = activeInputRef.current.value;
    }

    // Now check each value against its auction's capacity
    Object.entries(allQuantities).forEach(([auctionId, value]) => {
      if (!value || value.trim() === "") return;

      const numValue = parseFloat(value);
      const auctionData = rowData.find(
        (auction: ISourceAuction) => auction.auctionId === auctionId
      );
      const maxCapacity = auctionData?.availableAllocatedCapacity || 0;

      if (numValue > maxCapacity) {
        exceedingAuctions.push({
          auctionId,
          value,
          maxCapacity,
        });
      }
    });

    return exceedingAuctions;
  };

  return {
    quantities,
    setQuantities,
    editingAuctionId,
    setEditingAuctionId,
    isCancelling,
    setIsCancelling,
    activeInputRef,
    handleStartEdit,
    handleCancelEdit,
    handleSaveValue,
    getExceedingCapacityAuctions
  };
}